import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import moment from "moment";
import ReactVortoxSpinner from "../common-components/ReactVortoxSpinner";
import CommonStructuredV2 from "../common-components/CommonStructuredV2";
import { EyeIcon } from "../../images/icons/icons";
import { TrackingTimeFormat } from "./TrackingUtils";
import "../../styles/tracking-system/location-tracking.css";
import { useGetFetchNew } from "../../data/Queries";
import { trackingSystemBreakLogs } from "../../data/ApiEndpoints";

const BreakLogs = ({ userId, userInfoData, onCloseModal, date }) => {
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  const {
    data: breakLogsData,
    isLoading: breakLogsLoading,
    refetch: refetchBreakLogs,
  } = useGetFetchNew({
    token: userInfoData?.token,
    key: "break_logs",
    url: trackingSystemBreakLogs,
    filters: {
      user_id: userId,
      limit: pageLimit || 10,
      page: isPage + 1,
      start_date: date,
      end_date: date,
    },
  });

  useEffect(() => {
    refetchBreakLogs();
  }, [date, userId, pageLimit, isPage]);

  // Format duration display
  const formatDuration = (duration) => {
    if (!duration) return "-";
    if (duration < 60) return `${duration}m`;
    const hours = Math.floor(duration / 60);
    const mins = duration % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Calculate actual break duration
  const calculateActualDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "-";
    const start = moment(startTime);
    const end = moment(endTime);
    const diffMinutes = end.diff(start, "minutes");
    return formatDuration(diffMinutes);
  };

  // Get break status color
  const getBreakStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return { bg: "#28a745", color: "#fff" };
      case "completed":
        return { bg: "#007bff", color: "#fff" };
      case "expired":
        return { bg: "#dc3545", color: "#fff" };
      case "cancelled":
        return { bg: "#6c757d", color: "#fff" };
      default:
        return { bg: "#f8f9fa", color: "#495057" };
    }
  };

  // Format shift type
  const formatShiftType = (shiftType) => {
    if (!shiftType) return "";
    return shiftType
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const breakLogsColumns = [
    {
      dataField: "name",
      text: <p className="table__header">Employee</p>,
      formatter: (cell, row) => {
        return (
          <div
            className=""
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <p className="tb-data font-weight-bold">{row?.user_name || "-"}</p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <small className="tb-data text-muted">{row?.mobile || ""}</small>
              <span className="gym-badge ">{row?.gym_name || ""}</span>
            </div>
          </div>
        );
      },
    },
    {
      dataField: "break_name",
      text: <p className="table__header">Break Type</p>,
      formatter: (cell, row) => {
        return (
          <div className="break-type-cell">
            <p className="tb-data table__Text font-weight-bold">
              {row?.break_name || "-"}
            </p>
            {row?.break_description && (
              <small className="text-muted">{row.break_description}</small>
            )}
            {/* <div className="shift-info">
              <span className="shift-type-badge">
                {formatShiftType(row?.shift_type)}
              </span>
            </div> */}
          </div>
        );
      },
    },
    {
      dataField: "start_time",
      text: <p className="table__header">Start Time</p>,
      formatter: (cell, row) => {
        return (
          <div className="time-cell align-center">
            <p className="tb-data">
              {/* {moment(row?.date_added, "YYYY-MM-DD HH:mm:ss").format("h:mm A")} */}
              {moment(row?.start_time).format("h:mm a")}
            </p>
          </div>
        );
      },
    },
    {
      dataField: "expected_end_time",
      text: <p className="table__header">Expected End Time</p>,
      formatter: (cell, row) => {
        return (
          <div className="time-cell align-center">
            <p className="tb-data">
              {row?.expected_end_time
                ? moment(row.expected_end_time).format("hh:mm A")
                : ""}
            </p>
          </div>
        );
      },
    },
    {
      dataField: "break_duration",
      text: <p className="table__header">Duration</p>,
      formatter: (cell, row) => {
        const expectedDuration = formatDuration(row?.break_duration);
        const actualDuration = calculateActualDuration(
          row?.start_time,
          row?.actual_end_time
        );

        return (
          <div className="duration-cell">
            <div className="duration-info">
              <span className="expected-duration">
                Expected: {expectedDuration}
              </span>
              {row?.actual_end_time && (
                <span className="actual-duration">
                  Actual: {actualDuration}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      dataField: "break_status",
      text: <p className="table__header">Status</p>,
      formatter: (cell, row) => {
        const statusColors = getBreakStatusColor(row?.break_status);
        return (
          <span
            className="tb-data break-status-badge"
            style={{
              background: statusColors.bg,
              color: statusColors.color,
              padding: "6px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: "600",
              textTransform: "capitalize",
            }}
          >
            {row?.break_status || "Unknown"}
          </span>
        );
      },
    },
    {
      dataField: "date_added",
      text: <p className="table__header">Added On</p>,
      formatter: (cell, row) => {
        return (
          <div className="date-cell">
            <p className="tb-data table__Text">
              {row?.date_added
                ? moment(row.date_added).format("DD-MM-YYYY")
                : "-"}
            </p>
            <small className="time-display">
              {row?.date_added ? TrackingTimeFormat(row.date_added) : ""}
            </small>
          </div>
        );
      },
    },
    // {
    //   dataField: "day_timeline",
    //   text: <p className="table__header">Day Timeline</p>,
    //   formatter: (cell, row) => {
    //     return (
    //       <div
    //         className="underline"
    //         onClick={(e) => {
    //           e.stopPropagation();
    //           // handleViewBreakDetails(row);
    //         }}
    //       >
    //         <span className="timeline-icon">📊</span>
    //         <span>Click & View</span>
    //       </div>
    //     );
    //   },
    // },

    // {
    //   dataField: "action",
    //   text: <p className="table__header">Action</p>,
    //   formatter: (cell, row) => {
    //     return (
    //       <div className="actions-cell align-center">
    //         <div className="table-actions">
    //           <button
    //             className="table-action-btn view-action"
    //             onClick={(e) => {
    //               e.stopPropagation();
    //               handleViewBreakDetails(row);
    //             }}
    //             title="View Break Details"
    //           >
    //             <EyeIcon />
    //           </button>
    //         </div>
    //       </div>
    //     );
    //   },
    // },
  ];

  return (
    <div className="createNewCTOModalContainer mod-width-70p">
      {/* <div className="break-logs-container"> */}
      <Container className="bg-cloud-k gobal-container" fluid>
        <Row>
          <Col>
            <CommonStructuredV2
              pageHeading={"Break Logs"}
              subHeading={
                "View and track all employee break activities and timings"
              }
              isReload={() => refetchBreakLogs()}
              onCloseModal={onCloseModal}
            />
          </Col>
        </Row>

        <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table break-logs-table-container">
          {breakLogsLoading ? (
            <ReactVortoxSpinner />
          ) : (
            <PaymentLinkTable
              tableColumns={breakLogsColumns}
              tableData={breakLogsData?.data || []}
              tablePagination={breakLogsData?.pagination}
              isPage={isPage}
              setIsPage={(value) => setIsPage(value)}
              setPageLimit={setPageLimit}
              pageLimit={pageLimit}
              isFirstColSticky={true}
            />
          )}
        </div>

        {breakLogsData?.data?.length === 0 && !breakLogsLoading && (
          <div className="break-logs-empty-state">
            <div className="empty-icon">📋</div>
            <h5>No Break Logs Found</h5>
            <p>No break activities found for the selected criteria.</p>
          </div>
        )}
      </Container>
    </div>
  );
};

export default BreakLogs;
